import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Plus, Copy, Eye, EyeOff, Trash2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIPAccountsPage = () => {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [newAccount, setNewAccount] = useState({
    sip_name: "",
    sip_secret: ""
  });

  const fetchAccounts = async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const res = await axios.get(`${API}/user/sip`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setAccounts(res.data.accounts || []);
      }
    } catch (error) {
      toast.error("Failed to fetch SIP accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [token]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const res = await axios.post(`${API}/user/sip`, newAccount, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        toast.success("SIP account created successfully!");
        setDialogOpen(false);
        setNewAccount({ sip_name: "", sip_secret: "" });
        fetchAccounts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create SIP account");
    } finally {
      setCreating(false);
    }
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let password = "";
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewAccount({ ...newAccount, sip_secret: password });
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const togglePassword = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div data-testid="sip-accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">SIP Accounts</h1>
          <p className="text-slate-500 mt-1">Manage your SIP/VoIP accounts</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid="create-sip-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create SIP Account
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New SIP Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="sip_name">SIP Username</Label>
                <Input
                  id="sip_name"
                  value={newAccount.sip_name}
                  onChange={(e) => setNewAccount({ ...newAccount, sip_name: e.target.value })}
                  placeholder="e.g., myphone1"
                  required
                  className="mt-1"
                  data-testid="sip-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="sip_secret">SIP Password</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="sip_secret"
                    value={newAccount.sip_secret}
                    onChange={(e) => setNewAccount({ ...newAccount, sip_secret: e.target.value })}
                    placeholder="Enter password"
                    required
                    minLength={6}
                    data-testid="sip-secret-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={generatePassword}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <p className="font-medium text-slate-700 mb-2">SIP Configuration:</p>
                <p className="text-slate-600">Domain: <span className="font-mono">sip.cyberxcalls.com</span></p>
                <p className="text-slate-600">Port: <span className="font-mono">5060</span></p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-red-600 hover:bg-red-700"
                  disabled={creating}
                  data-testid="create-sip-submit"
                >
                  {creating ? "Creating..." : "Create Account"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* SIP Domain Info */}
      <div className="bg-slate-900 text-white rounded-xl p-6 mb-8">
        <h3 className="font-semibold mb-4">SIP Server Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">SIP Domain</p>
            <p className="font-mono text-lg">sip.cyberxcalls.com</p>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">SIP Port</p>
            <p className="font-mono text-lg">5060 (UDP/TCP)</p>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">WebSocket</p>
            <p className="font-mono text-lg">wss://sip.cyberxcalls.com:8089/ws</p>
          </div>
        </div>
      </div>

      {/* Accounts List */}
      {loading ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full mx-auto" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
          <Phone className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No SIP Accounts</h3>
          <p className="text-slate-500 mb-6">Create your first SIP account to start making calls</p>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create SIP Account
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {accounts.map((account, i) => (
            <motion.div
              key={account.id || i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{account.name || account.username}</h4>
                    <p className="text-sm text-slate-500">@sip.cyberxcalls.com</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                    <span className="text-sm text-slate-600">Password:</span>
                    <span className="font-mono text-sm">
                      {showPasswords[account.id || i] ? (account.secret || '••••••••') : '••••••••'}
                    </span>
                    <button
                      onClick={() => togglePassword(account.id || i)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      {showPasswords[account.id || i] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(account.name || account.username, "Username")}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SIPAccountsPage;
